import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import TextInput from 'src/components/TextInput';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { round } from 'src/utils';
import { getBgColor } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const UOMTextField: FC<GridCellProps> = (props: GridCellProps) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);
  const hasError = useMemo(() => !!dataItem?.errors?.uom?.length, [dataItem?.errors?.uom]);
  const { value, handleChange } = useDelayedInputOnchange(props, true);
  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item.item.uom.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);

  const isEdited = useMemo(
    () => matchingItem[0]?.item?.uom?.value?.toLowerCase()?.trim() !== value.toLowerCase()?.trim(),
    [matchingItem, value]
  );

  const matchItemIndex = matchingItem.map(
    (item) => Number(item.item?.itemSequenceNumber?.value) - 1
  );
  const handleHover = useCallback(() => {
    handleHoverOcrField('uom', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);

  return (
    <GridCellBase
      {...props}
      className={getBgColor(dataItem?.errors?.uom, [], dataItem?.informations?.uom)}
      disableToolTip
    >
      <div className="flex flex-row gap-1 items-center justify-between">
        <div data-tooltip-id={'global-tooltip'} data-tooltip-content={value}>
          {isInvoiceEditDisabled ? (
            <span>{value}</span>
          ) : (
            <TextInput
              type="text"
              value={value}
              onChange={handleChange}
              valid={!hasError}
              onFocus={handleHover}
              ocr-data={`uom:${matchItemIndex}`}
            />
          )}
        </div>
        {initialInvoice.ocrGenerated && ocrConfidence !== 0 && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default UOMTextField;
